// ─────────────────────────────────────────────────────────────────────────────
// Lazy loaders for the two Firebase products that are not needed to render a
// page: Cloud Storage and Cloud Functions.
//
// `src/firebase.js` lands in the main chunk — AuthContext imports it on the
// first tick — so anything it initializes at module scope ships to every
// visitor, including a signed-out one on the marketing page. Storage used to be
// a module-scope `getStorage(app)` there, and Functions rode in behind it
// through AuthContext → userService's module-scope `getFunctions(app)`. "/" was
// downloading both SDKs to render a page that touches neither.
//
// Both now sit behind `import()`, which webpack splits into its own chunk and
// fetches on first use — the first file upload, the first callable. Auth and
// Firestore stay eager in firebase.js deliberately: AuthContext needs both
// before anything can render, so deferring them would buy nothing and cost a
// round trip on the app's critical path.
// ─────────────────────────────────────────────────────────────────────────────

import app from './firebase';

// ── Cloud Storage ────────────────────────────────────────────────────────────

let storagePromise = null;

// Resolves to the storage instance plus the handful of module functions the app
// actually uses, listed explicitly rather than re-exported wholesale so this
// stays an honest inventory of BoneFlow's Storage surface. Memoized on the
// promise, so concurrent callers share one import; after the first call it
// resolves from webpack's module cache and `getStorage(app)` hands back the
// same instance regardless.
export function loadStorage() {
  if (!storagePromise) {
    storagePromise = import('firebase/storage').then((m) => ({
      storage: m.getStorage(app),
      ref: m.ref,
      uploadBytes: m.uploadBytes,
      uploadBytesResumable: m.uploadBytesResumable,
      getDownloadURL: m.getDownloadURL,
      deleteObject: m.deleteObject,
    }));
  }
  return storagePromise;
}

// ── Cloud Functions ──────────────────────────────────────────────────────────

let functionsPromise = null;

function loadFunctions() {
  if (!functionsPromise) {
    functionsPromise = import('firebase/functions').then((m) => ({
      functions: m.getFunctions(app),
      httpsCallable: m.httpsCallable,
    }));
  }
  return functionsPromise;
}

// Every callable in the app was the same three lines — build the callable, await
// it, return `.data` — so that shape is the helper. Errors propagate untouched:
// callers catch the SDK's FunctionsError and read `.code` off it, and wrapping
// them here would break that.
export async function callFunction(name, payload = {}, options) {
  const { functions, httpsCallable } = await loadFunctions();
  const fn = httpsCallable(functions, name, options);
  const result = await fn(payload);
  return result.data;
}
